import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { Search as SearchIcon, User, Users, Eye, Lock, Calendar, Filter, Plus, Target, Trash2, Check, Download, Brain, Globe, MapPin, Zap, TrendingUp, Tag, UserCircle2, Sparkles, Camera, Utensils, Plane, Laptop, Dumbbell, Music, Palette, ShoppingBag, Heart, Briefcase, Home, Gamepad2, BookOpen, Sparkle, X, Contact, ChevronDown, ChevronRight, Activity, MessageCircle, UserPlus, UserMinus, Loader } from 'lucide-react';
import MapView from '../components/utilities/MapView';
import SharedPageHeader from '../components/layout/PageHeader';
import { getProfilePicUrl, getLocalProfilePicUrl, getScreenshotUrl } from '../config/imageUrls';

const PageContainer = styled.div`
  padding: 20px;
  max-width: 100%;
  margin: 0 auto;
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 30px;
  align-items: start;
`;

const LeftColumn = styled.div`
  flex: 1;
`;

const RightColumn = styled.div`
  position: sticky;
  top: 30px;
  max-height: calc(100vh - 60px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }
`;



const SearchBox = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 15px 20px 15px 50px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(165, 145, 239, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
`;



const FilterSelect = styled.select`
  padding: 8px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary);
  }

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(165, 145, 239, 0.1);
  }

  option {
    background: var(--bg);
    color: var(--text-primary);
  }
`;



const ResultsContainer = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border);
`;

const ResultsCount = styled.div`
  color: var(--primary);
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProfileCard = styled.div`
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 10px;
  padding: 14px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary);
    background: var(--surface);
  }
`;

const ProfilePicture = styled.div`
  width: 60px;
  height: 60px;
  min-width: 60px;
  min-height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border: 3px solid rgba(165, 145, 239, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 24px;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.4);
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
  
  img {
    width: 54px;
    height: 54px;
    object-fit: cover;
    display: block;
    border-radius: 50%;
    position: absolute;
    inset: 0;
    margin: auto;
  }
`;

const Username = styled.a`
  color: var(--text-primary);
  font-weight: 700;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: var(--primary);
    transform: translateX(2px);
  }
`;







const StatsRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  margin-top: 4px;
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.div`
  color: #888;
  font-size: 11px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  color: var(--primary);
  font-size: 18px;
  font-weight: 700;
`;





const StatusBadge = styled.div`
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  text-align: center;
  background: ${props => props.verified ? 'rgba(102, 230, 135, 0.2)' : 'rgba(136, 136, 136, 0.2)'};
  color: ${props => props.verified ? 'var(--success)' : '#888'};
  border: 1px solid ${props => props.verified ? 'rgba(102, 230, 135, 0.3)' : 'rgba(136, 136, 136, 0.3)'};
`;

const SortBar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 20px;
`;

const SortLabel = styled.div`
  color: var(--text-muted);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SortButton = styled.button`
  background: ${props => props.active ? 'var(--primary)' : 'var(--surface)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : 'var(--border)'};
  border-radius: 8px;
  padding: 6px 14px;
  color: ${props => props.active ? '#fff' : 'var(--text-secondary)'};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary);
    color: ${props => props.active ? '#fff' : 'var(--text-primary)'};
  }
`;



const RangeFilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RangeFilterLabel = styled.div`
  color: var(--text-muted);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RangeInputs = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const RangeInput = styled.input`
  width: 100px;
  padding: 8px 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(165, 145, 239, 0.1);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const RangeSeparator = styled.div`
  color: var(--primary);
  font-weight: 700;
`;

const DropdownWrap = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownTrigger = styled.button`
  padding: 7px 10px;
  background: ${p => p.$active ? 'rgba(165,145,239,0.15)' : 'var(--surface)'};
  border: 1px solid ${p => p.$active ? 'var(--primary)' : 'var(--border)'};
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  min-width: 0;
  &:hover { border-color: var(--primary); }
`;

const DropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 260px;
  max-height: 380px;
  overflow-y: auto;
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.18);
  z-index: 100;
  padding: 6px 0;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const DropdownSection = styled.div`
  padding: 4px 10px;
  &:not(:last-child) { border-bottom: 1px solid var(--border); margin-bottom: 2px; }
`;

const DropdownSectionTitle = styled.div`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--text-muted);
  padding: 4px 6px 2px;
`;

const DropdownCheckItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: var(--text-primary);
  transition: background 0.15s;
  &:hover { background: rgba(165,145,239,0.08); }
  input[type="checkbox"], input[type="radio"] {
    width: 14px; height: 14px; cursor: pointer; accent-color: var(--primary); flex-shrink: 0;
  }
`;

const CountryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
  transition: background 0.15s;
  &:hover { background: rgba(165,145,239,0.08); }
`;

const CityList = styled.div`
  padding-left: 22px;
`;

const ActiveFilterCount = styled.span`
  background: var(--primary);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 1px 6px;
  min-width: 16px;
  text-align: center;
`;

const ActionSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 16px;
`;

const ActionOption = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 12px 8px;
  border-radius: 10px;
  border: 2px solid ${p => p.$active ? 'var(--primary)' : 'rgba(165, 145, 239, 0.15)'};
  background: ${p => p.$active ? 'rgba(165, 145, 239, 0.18)' : 'rgba(165, 145, 239, 0.04)'};
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    border-color: var(--primary);
    background: rgba(165, 145, 239, 0.12);
  }
`;

const RadioButton = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
`;

const ActionLabel = styled.span`
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ActionDescription = styled.div`
  color: var(--text-secondary);
  font-size: 10px;
  line-height: 1.3;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #888;
  font-size: 16px;
`;

const TargetPanel = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 16px rgba(165, 145, 239, 0.4);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(165, 145, 239, 0.6), transparent);
  }
`;

const PanelTitle = styled.h2`
  color: var(--primary);
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SelectedProfilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
  max-height: 400px;
  overflow-y: auto;
`;

const SelectedProfileItem = styled.div`
  background: rgba(165, 145, 239, 0.1);
  border: 1px solid rgba(165, 145, 239, 0.3);
  border-radius: 10px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(165, 145, 239, 0.15);
    border-color: var(--primary);
  }
`;

const ProfileName = styled.div`
  color: #fff;
  font-weight: 600;
  font-size: 14px;
`;

const RemoveButton = styled.button`
  background: rgba(244, 67, 54, 0.2);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--error);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;

  &:hover {
    background: rgba(244, 67, 54, 0.3);
    border-color: var(--error);
  }
`;

const AddTargetsButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border: 1px solid rgba(165, 145, 239, 0.6);
  border-radius: 12px;
  padding: 15px;
  color: white;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.4);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  }

  &:hover {
    background: linear-gradient(135deg, var(--secondary), var(--accent));
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(165, 145, 239, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SelectCheckbox = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${props => props.selected ? 'linear-gradient(135deg, var(--primary), var(--secondary))' : 'rgba(165, 145, 239, 0.5)'};
  border: 2px solid ${props => props.selected ? 'var(--primary)' : 'rgba(165, 145, 239, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    border-color: var(--primary);
    background: ${props => props.selected ? 'linear-gradient(135deg, var(--primary), var(--secondary))' : 'rgba(165, 145, 239, 0.2)'};
  }
`;

const EmptySelection = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #888;
  font-size: 14px;
`;

const TemporaryTargetsPanel = styled.div`
  background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg) 100%);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: blur(10px);
  margin-top: 20px;
  box-shadow: 0 4px 16px rgba(165, 145, 239, 0.4);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, transparent, rgba(165, 145, 239, 0.6), transparent);
  }
`;

const TemporaryTargetsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
  max-height: 300px;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(165, 145, 239, 0.2);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(165, 145, 239, 0.5);
    border-radius: 4px;
    
    &:hover {
      background: rgba(165, 145, 239, 0.7);
    }
  }
`;

const TemporaryTargetItem = styled.div`
  background: linear-gradient(135deg, rgba(165, 145, 239, 0.1), rgba(168, 85, 247, 0.05));
  border: 1px solid rgba(165, 145, 239, 0.3);
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(165, 145, 239, 0.4), transparent);
  }

  &:hover {
    background: linear-gradient(135deg, rgba(165, 145, 239, 0.15), rgba(168, 85, 247, 0.1));
    border-color: var(--primary);
    transform: translateX(2px);
  }
`;

const ConfirmTargetsButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  border: 1px solid rgba(165, 145, 239, 0.6);
  border-radius: 12px;
  padding: 15px;
  color: white;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 4px 12px rgba(165, 145, 239, 0.4);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  }

  &:hover {
    background: linear-gradient(135deg, var(--secondary), var(--accent));
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(165, 145, 239, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const AccountSelector = styled.select`
  width: 100%;
  padding: 12px 16px;
  background: var(--surface);
  border: 2px solid rgba(165, 145, 239, 0.3);
  border-radius: 10px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary);
    background: rgba(165, 145, 239, 0.15);
  }

  &:hover {
    border-color: var(--primary);
  }

  option {
    background: var(--surface) !important;
    color: var(--text-primary) !important;
    padding: 10px;
    font-size: 14px;
    font-weight: 600;
  }
  
  /* Force white text for selected option */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--bg);
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--primary);
    border-radius: 4px;
  }
`;

const SelectorLabel = styled.div`
  color: var(--primary);
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const openExternal = (url) => {
  try {
    const { shell } = window.require('electron');
    shell.openExternal(url);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

function Search() {
  const [searchTerm, setSearchTerm] = useState('');
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [loadedResults, setLoadedResults] = useState(0);
  const [filterVerified] = useState('all');
  const [filterPrivate] = useState('all');
  const [selectedProfiles, setSelectedProfiles] = useState([]);
  const [temporaryTargets, setTemporaryTargets] = useState([]);
  const [addingTargets, setAddingTargets] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [followersMin, setFollowersMin] = useState('');
  const [followersMax, setFollowersMax] = useState('');
  const [followingMin, setFollowingMin] = useState('');
  const [followingMax, setFollowingMax] = useState('');
  const [postsMin, setPostsMin] = useState('');
  const [postsMax, setPostsMax] = useState('');
  const [selectedAction, setSelectedAction] = useState('add-targets');
  const [limitPerPage, setLimitPerPage] = useState(100);
  const [filterLanguage, setFilterLanguage] = useState('');
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [filterHasLocation, setFilterHasLocation] = useState(false);
  const [filterHasCategories, setFilterHasCategories] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAllCategories, setShowAllCategories] = useState(null);
  const [filterVisitedBy, setFilterVisitedBy] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableVisitedByAccounts, setAvailableVisitedByAccounts] = useState([]);
  const [availableSources, setAvailableSources] = useState([]);
  const [countryCitiesData, setCountryCitiesData] = useState({});
  const [filterHasBiography, setFilterHasBiography] = useState('all');
  const [filterGeminiAnalyzed, setFilterGeminiAnalyzed] = useState('all');
  const [filterBusiness, setFilterBusiness] = useState('all');
  const [filterAccountType, setFilterAccountType] = useState('all');
  const [filterGender, setFilterGender] = useState('all');
  const [filterEngagement, setFilterEngagement] = useState('all');
  const [filterIsTarget, setFilterIsTarget] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [profileStats, setProfileStats] = useState(null);
  const [topCategories, setTopCategories] = useState([]);
  const [topCountries, setTopCountries] = useState([]);
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [expandedCountries, setExpandedCountries] = useState({});
  const [expandedProfile, setExpandedProfile] = useState(null);
  const [profileActions, setProfileActions] = useState(null);
  const [profileActionsLoading, setProfileActionsLoading] = useState(false);
  const criteriaRef = useRef(null);
  const locationRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchProfiles();
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterVerified, filterPrivate, followersMin, followersMax, followingMin, followingMax, postsMin, postsMax, limitPerPage, filterLanguage, selectedCountries, selectedCities, filterHasLocation, filterHasCategories, filterCategory, filterStatus, filterVisitedBy, filterSource, filterHasBiography, filterGeminiAnalyzed, filterBusiness, filterGender, filterEngagement, filterIsTarget, filterDateFrom, filterDateTo, filterAccountType]);

  // Load actions when a profile is expanded
  useEffect(() => {
    if (!expandedProfile) { setProfileActions(null); return; }
    setProfileActionsLoading(true);
    axios.get(`/api/profiles-search/${encodeURIComponent(expandedProfile)}/actions`)
      .then(res => setProfileActions(res.data))
      .catch(() => setProfileActions(null))
      .finally(() => setProfileActionsLoading(false));
  }, [expandedProfile]);

  // Click-outside to close dropdowns
  useEffect(() => {
    const handler = (e) => {
      if (criteriaRef.current && !criteriaRef.current.contains(e.target)) setCriteriaOpen(false);
      if (locationRef.current && !locationRef.current.contains(e.target)) setLocationOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    fetchAccounts();
    fetchCategories();
    fetchVisitedByAccounts();
    fetchSources();
    fetchCitiesByCountry();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/instagram-accounts');
      setAccounts(response.data.accounts || []);
      if (response.data.accounts.length > 0) {
        setSelectedAccountId(response.data.accounts[0].id);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des comptes:', error);
    }
  };


  const fetchCategories = async () => {
    try {
      const response = await axios.get('/api/categories');
      setAvailableCategories(response.data.categories || []);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des catégories:', error);
      setAvailableCategories([]);
    }
  };

  const fetchVisitedByAccounts = async () => {
    try {
      const response = await axios.get('/api/visited-by-accounts');
      setAvailableVisitedByAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des comptes visited_by:', error);
      setAvailableVisitedByAccounts([]);
    }
  };

  const fetchSources = async () => {
    try {
      const response = await axios.get('/api/sources');
      setAvailableSources(response.data.sources || []);
    } catch (error) {
      console.error('❌ Erreur lors du chargement des sources:', error);
      setAvailableSources([]);
    }
  };

  const fetchCitiesByCountry = async () => {
    try {
      const response = await axios.get('/api/cities-by-country');
      setCountryCitiesData(response.data.countryCities || {});
    } catch (error) {
      console.error('❌ Erreur lors du chargement des villes par pays:', error);
      setCountryCitiesData({});
    }
  };

  const fetchProfiles = async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterVerified !== 'all') params.append('verified', filterVerified);
      if (filterPrivate !== 'all') params.append('private', filterPrivate);
      if (followersMin) params.append('followers_min', followersMin);
      if (followersMax) params.append('followers_max', followersMax);
      if (followingMin) params.append('following_min', followingMin);
      if (followingMax) params.append('following_max', followingMax);
      if (postsMin) params.append('posts_min', postsMin);
      if (postsMax) params.append('posts_max', postsMax);
      if (limitPerPage) params.append('limit', limitPerPage);
      if (filterLanguage) params.append('language', filterLanguage);
      if (selectedCountries.length > 0) params.append('countries', selectedCountries.join(','));
      if (selectedCities.length > 0) params.append('cities', selectedCities.join(','));
      if (filterHasLocation) params.append('has_location', 'true');
      if (filterHasCategories) params.append('has_categories', 'true');
      if (filterCategory) params.append('category', filterCategory);
      if (filterStatus !== 'all') {
        const [verified, isPrivate] = filterStatus.split('-');
        if (verified !== 'all') params.append('verified', verified);
        if (isPrivate !== 'all') params.append('private', isPrivate);
      }
      if (filterVisitedBy) params.append('visited_by', filterVisitedBy);
      if (filterSource) params.append('source', filterSource);
      if (filterHasBiography !== 'all') params.append('has_biography', filterHasBiography);
      if (filterGeminiAnalyzed !== 'all') params.append('gemini_analyzed', filterGeminiAnalyzed);
      if (filterBusiness !== 'all') params.append('is_business', filterBusiness);
      if (filterAccountType !== 'all') params.append('account_type', filterAccountType);
      if (filterGender !== 'all') params.append('gender', filterGender);
      if (filterEngagement !== 'all') params.append('engagement_level', filterEngagement);
      if (filterIsTarget !== 'all') params.append('is_target', filterIsTarget);
      if (filterDateFrom) params.append('date_from', filterDateFrom);
      if (filterDateTo) params.append('date_to', filterDateTo);

      const response = await axios.get(`/api/profiles-search?${params.toString()}`, { signal: controller.signal });
      const fetchedProfiles = response.data.profiles || [];
      setProfiles(sortProfiles(fetchedProfiles, sortBy));
      setTotalResults(response.data.total || 0);
      setLoadedResults(response.data.loaded || fetchedProfiles.length);
      if (response.data.stats) setProfileStats(response.data.stats);
      if (response.data.topCategories) setTopCategories(response.data.topCategories);
      if (response.data.topCountries) setTopCountries(response.data.topCountries);
    } catch (error) {
      if (axios.isCancel(error) || error?.name === 'AbortError' || error?.name === 'CanceledError') return;
      console.error('❌ Erreur lors de la recherche:', error);
      setProfiles([]);
      setTotalResults(0);
      setLoadedResults(0);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toggleProfileSelection = (profile) => {
    setSelectedProfiles(prev => {
      const isSelected = prev.some(p => p.username === profile.username);
      if (isSelected) {
        return prev.filter(p => p.username !== profile.username);
      } else {
        return [...prev, profile];
      }
    });
  };

  const removeSelectedProfile = (username) => {
    setSelectedProfiles(prev => prev.filter(p => p.username !== username));
  };

  const moveToTemporaryTargets = () => {
    if (selectedProfiles.length === 0) return;
    
    // Ajouter les profils sélectionnés à la liste temporaire (éviter les doublons)
    setTemporaryTargets(prev => {
      const newTargets = [...prev];
      selectedProfiles.forEach(profile => {
        if (!newTargets.some(t => t.username === profile.username)) {
          newTargets.push(profile);
        }
      });
      return newTargets;
    });
    
    // Vider la sélection
    setSelectedProfiles([]);
  };

  const removeTemporaryTarget = (username) => {
    setTemporaryTargets(prev => prev.filter(t => t.username !== username));
  };

  const exportToGoogleSheets = async () => {
    try {
      setAddingTargets(true);
      
      await axios.post('/api/export-to-sheets', {
        profiles: temporaryTargets.map(profile => ({
          username: profile.username,
          fullName: profile.full_name,
          biography: profile.biography,
          postsCount: profile.posts_count,
          followersCount: profile.followers_count,
          followingCount: profile.following_count,
          isPrivate: profile.is_private,
          isVerified: profile.is_verified,
          visitedAt: profile.visited_at,
          mainCategory: profile.main_category,
          categories: profile.categories,
          locationCity: profile.location_city,
          phoneNumber: profile.phone_number,
          geminiSummary: profile.gemini_summary
        }))
      });
      
      alert(`✅ ${temporaryTargets.length} profil(s) exporté(s) vers Google Sheets`);
      setTemporaryTargets([]);
      setSelectedProfiles([]);
    } catch (error) {
      console.error('❌ Erreur lors de l\'export:', error);
      alert('❌ Erreur lors de l\'export vers Google Sheets');
    } finally {
      setAddingTargets(false);
    }
  };

  const analyzeWithGemini = async () => {
    try {
      setAddingTargets(true);
      
      const profiles = temporaryTargets.map(profile => ({
        username: profile.username,
        full_name: profile.full_name || '',
        biography: profile.biography || '',
        followers_count: profile.followers_count || 0,
        following_count: profile.following_count || 0,
        posts_count: profile.posts_count || 0,
        is_verified: profile.is_verified || false,
        is_private: profile.is_private || false
      }));
      
      console.log(`🤖 Analyse de ${profiles.length} profils avec Gemini...`);
      
      const BATCH_SIZE = 20;
      const totalBatches = Math.ceil(profiles.length / BATCH_SIZE);
      
      let totalUpdated = 0;
      let totalErrors = 0;
      
      for (let i = 0; i < totalBatches; i++) {
        const start = i * BATCH_SIZE;
        const end = Math.min(start + BATCH_SIZE, profiles.length);
        const batch = profiles.slice(start, end);
        
        console.log(`📦 Batch ${i + 1}/${totalBatches}: ${batch.length} profils`);
        
        try {
          const response = await axios.post('/api/enrich-profiles', { profiles: batch });
          
          if (response.data.success) {
            totalUpdated += response.data.updated || 0;
            totalErrors += response.data.errors || 0;
            console.log(`✅ Batch ${i + 1}/${totalBatches} terminé: ${response.data.updated} profils enrichis`);
          }
          
        } catch (batchError) {
          console.error(`❌ Erreur batch ${i + 1}:`, batchError.message);
          totalErrors += batch.length;
        }
        
        if (i < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
      
      alert(`✅ Analyse terminée!\n${totalUpdated} profil(s) enrichi(s)${totalErrors > 0 ? `\n${totalErrors} erreur(s)` : ''}\n\nLes colonnes primary_language, location_country, location_region, gender, categories et engagement_level ont été mises à jour dans instagram_profile_visits.`);
      setTemporaryTargets([]);
      setSelectedProfiles([]);
      
      fetchProfiles();
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'analyse:', error);
      alert('❌ Erreur lors de l\'analyse avec Gemini');
    } finally {
      setAddingTargets(false);
    }
  };

  const exportToCrm = async () => {
    try {
      setAddingTargets(true);
      const response = await axios.post('/api/prospects/bulk', {
        profiles: temporaryTargets.map(profile => ({
          username: profile.username,
          full_name: profile.full_name,
          biography: profile.biography,
          posts: profile.posts_count,
          followers: profile.followers_count,
          following: profile.following_count,
          is_verified: profile.is_verified,
          is_business: profile.is_business_account,
          category: profile.main_category,
          categories: profile.categories,
          location_city: profile.location_city,
          location_country: profile.location_country,
          location_region: profile.location_region,
          language: profile.primary_language
        })),
        source: 'outbound'
      });
      alert(`✅ ${response.data.imported} prospect(s) importé(s) dans le CRM${response.data.skipped > 0 ? ` (${response.data.skipped} déjà existant(s))` : ''}`);
      setTemporaryTargets([]);
      setSelectedProfiles([]);
    } catch (error) {
      console.error('❌ Erreur lors de l\'import CRM:', error);
      alert('❌ Erreur lors de l\'import dans le CRM');
    } finally {
      setAddingTargets(false);
    }
  };

  const addToInteractList = async () => {
    if (!selectedAccountId) {
      alert('⚠️ Veuillez sélectionner un compte Instagram');
      return;
    }
    try {
      setAddingTargets(true);
      const profiles = temporaryTargets.map(p => ({
        username: p.username,
        source: p.source || null,
        source_type: 'user'
      }));
      const res = await axios.post(`/api/account-lists/interact-db/${selectedAccountId}/add`, { profiles });
      alert(`✅ ${res.data.added} profil(s) ajouté(s) à la liste d'interaction${res.data.skipped > 0 ? ` (${res.data.skipped} déjà présent(s))` : ''}\nTotal en attente: ${res.data.pending_count}`);
      setTemporaryTargets([]);
      setSelectedProfiles([]);
    } catch (error) {
      console.error('❌ Erreur ajout interact list:', error);
      alert('❌ Erreur lors de l\'ajout à la liste d\'interaction');
    } finally {
      setAddingTargets(false);
    }
  };

  const confirmTargets = async () => {
    if (selectedAction === 'google-sheets') {
      await exportToGoogleSheets();
      return;
    }
    
    if (selectedAction === 'analyze-gemini') {
      await analyzeWithGemini();
      return;
    }

    if (selectedAction === 'send-to-crm') {
      await exportToCrm();
      return;
    }

    if (selectedAction === 'interact-list') {
      await addToInteractList();
      return;
    }
    
    if (!selectedAccountId) {
      alert('⚠️ Veuillez sélectionner un compte Instagram');
      return;
    }
    
    try {
      setAddingTargets(true);
      
      let successCount = 0;
      let duplicateCount = 0;
      
      for (const profile of temporaryTargets) {
        try {
          await axios.post('/api/add-target', {
            username: profile.username,
            accountId: selectedAccountId,
            fullName: profile.full_name,
            biography: profile.biography,
            postsCount: profile.posts_count,
            followersCount: profile.followers_count,
            followingCount: profile.following_count,
            isPrivate: profile.is_private,
            isVerified: profile.is_verified,
            profilePicUrl: profile.profile_pic_url
          });
          successCount++;
        } catch (error) {
          if (error.response?.status === 409) {
            duplicateCount++;
          } else {
            console.error(`❌ Erreur pour @${profile.username}:`, error);
          }
        }
      }
      
      alert(`✅ ${successCount} cible(s) ajoutée(s)${duplicateCount > 0 ? ` (${duplicateCount} déjà existante(s))` : ''}`);
      setTemporaryTargets([]);
      setSelectedProfiles([]);
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout des cibles:', error);
      alert('❌ Erreur lors de l\'ajout des cibles');
    } finally {
      setAddingTargets(false);
    }
  };

  const isProfileSelected = (username) => {
    return selectedProfiles.some(p => p.username === username);
  };

  const sortProfiles = (profilesList, sortType) => {
    const sorted = [...profilesList];
    switch(sortType) {
      case 'date':
        return sorted.sort((a, b) => new Date(b.visited_at) - new Date(a.visited_at));
      case 'enriched':
        return sorted.sort((a, b) => {
          const dateA = a.gemini_analyzed_at ? new Date(a.gemini_analyzed_at) : new Date(0);
          const dateB = b.gemini_analyzed_at ? new Date(b.gemini_analyzed_at) : new Date(0);
          return dateB - dateA;
        });
      case 'followers':
        return sorted.sort((a, b) => (b.followers_count || 0) - (a.followers_count || 0));
      case 'following':
        return sorted.sort((a, b) => (b.following_count || 0) - (a.following_count || 0));
      case 'posts':
        return sorted.sort((a, b) => (b.posts_count || 0) - (a.posts_count || 0));
      case 'username':
        return sorted.sort((a, b) => (a.username || '').localeCompare(b.username || ''));
      default:
        return sorted;
    }
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
  };

  const getLanguageFlag = (lang) => {
    const flags = {
      'fr': '🇫🇷',
      'en': '🇬🇧',
      'es': '🇪🇸',
      'de': '🇩🇪',
      'it': '🇮🇹',
      'pt': '🇵🇹',
      'ar': '🇸🇦',
      'ja': '🇯🇵',
      'zh': '🇨🇳',
      'ru': '🇷🇺'
    };
    return flags[lang?.toLowerCase()] || '🌐';
  };

  const countryToIso = (country) => {
    if (!country) return null;
    const map = {
      'france': 'fr', 'allemagne': 'de', 'germany': 'de', 'espagne': 'es', 'spain': 'es',
      'italie': 'it', 'italy': 'it', 'portugal': 'pt', 'belgique': 'be', 'belgium': 'be',
      'suisse': 'ch', 'switzerland': 'ch', 'luxembourg': 'lu', 'pays-bas': 'nl', 'netherlands': 'nl',
      'royaume-uni': 'gb', 'united kingdom': 'gb', 'uk': 'gb', 'angleterre': 'gb', 'england': 'gb',
      'états-unis': 'us', 'united states': 'us', 'usa': 'us', 'etats-unis': 'us',
      'canada': 'ca', 'mexique': 'mx', 'mexico': 'mx', 'brésil': 'br', 'brazil': 'br',
      'japon': 'jp', 'japan': 'jp', 'chine': 'cn', 'china': 'cn', 'corée du sud': 'kr', 'south korea': 'kr',
      'inde': 'in', 'india': 'in', 'australie': 'au', 'australia': 'au',
      'maroc': 'ma', 'morocco': 'ma', 'tunisie': 'tn', 'tunisia': 'tn', 'algérie': 'dz', 'algeria': 'dz',
      'sénégal': 'sn', 'senegal': 'sn', 'côte d\'ivoire': 'ci', 'cameroun': 'cm', 'cameroon': 'cm',
      'russie': 'ru', 'russia': 'ru', 'turquie': 'tr', 'turkey': 'tr', 'türkiye': 'tr',
      'pologne': 'pl', 'poland': 'pl', 'roumanie': 'ro', 'romania': 'ro',
      'grèce': 'gr', 'greece': 'gr', 'autriche': 'at', 'austria': 'at',
      'irlande': 'ie', 'ireland': 'ie', 'danemark': 'dk', 'denmark': 'dk',
      'suède': 'se', 'sweden': 'se', 'norvège': 'no', 'norway': 'no', 'finlande': 'fi', 'finland': 'fi',
      'hongrie': 'hu', 'hungary': 'hu', 'tchéquie': 'cz', 'czech republic': 'cz', 'czechia': 'cz',
      'croatie': 'hr', 'croatia': 'hr', 'slovaquie': 'sk', 'slovakia': 'sk',
      'colombie': 'co', 'colombia': 'co', 'argentine': 'ar', 'argentina': 'ar', 'chili': 'cl', 'chile': 'cl',
      'pérou': 'pe', 'peru': 'pe', 'venezuela': 've',
      'émirats arabes unis': 'ae', 'united arab emirates': 'ae', 'uae': 'ae',
      'arabie saoudite': 'sa', 'saudi arabia': 'sa', 'qatar': 'qa',
      'liban': 'lb', 'lebanon': 'lb', 'egypte': 'eg', 'egypt': 'eg', 'israël': 'il', 'israel': 'il',
      'thaïlande': 'th', 'thailand': 'th', 'vietnam': 'vn', 'indonésie': 'id', 'indonesia': 'id',
      'malaisie': 'my', 'malaysia': 'my', 'philippines': 'ph', 'singapour': 'sg', 'singapore': 'sg',
      'nouvelle-zélande': 'nz', 'new zealand': 'nz', 'afrique du sud': 'za', 'south africa': 'za',
    };
    const key = country.toLowerCase().trim();
    if (map[key]) return map[key];
    if (key.length === 2) return key;
    return null;
  };

  const getCountryFlagUrl = (country) => {
    const iso = countryToIso(country);
    if (!iso) return null;
    return `https://flagcdn.com/w40/${iso}.png`;
  };

  const getCategoryIcon = (category) => {
    const cat = category.toLowerCase();
    
    // Fashion & Style
    if (cat.includes('fashion') || cat.includes('mode') || cat.includes('style')) {
      return { icon: ShoppingBag, color: 'var(--accent)' };
    }
    // Food & Cooking
    if (cat.includes('food') || cat.includes('cuisine') || cat.includes('cooking') || cat.includes('restaurant')) {
      return { icon: Utensils, color: 'var(--warning)' };
    }
    // Travel
    if (cat.includes('travel') || cat.includes('voyage') || cat.includes('tourism')) {
      return { icon: Plane, color: 'var(--info)' };
    }
    // Tech & Digital
    if (cat.includes('tech') || cat.includes('digital') || cat.includes('software') || cat.includes('coding')) {
      return { icon: Laptop, color: 'var(--primary)' };
    }
    // Fitness & Sport
    if (cat.includes('fitness') || cat.includes('sport') || cat.includes('workout') || cat.includes('gym')) {
      return { icon: Dumbbell, color: 'var(--success)' };
    }
    // Music
    if (cat.includes('music') || cat.includes('musique') || cat.includes('dj') || cat.includes('artist')) {
      return { icon: Music, color: 'var(--secondary)' };
    }
    // Art & Design
    if (cat.includes('art') || cat.includes('design') || cat.includes('creative') || cat.includes('artist')) {
      return { icon: Palette, color: 'var(--warning)' };
    }
    // Beauty & Lifestyle
    if (cat.includes('beauty') || cat.includes('beauté') || cat.includes('lifestyle') || cat.includes('makeup')) {
      return { icon: Sparkle, color: 'var(--accent)' };
    }
    // Photography
    if (cat.includes('photo') || cat.includes('photography')) {
      return { icon: Camera, color: 'var(--primary)' };
    }
    // Gaming
    if (cat.includes('gaming') || cat.includes('gamer') || cat.includes('esport')) {
      return { icon: Gamepad2, color: 'var(--primary)' };
    }
    // Business & Entrepreneur
    if (cat.includes('business') || cat.includes('entrepreneur') || cat.includes('startup')) {
      return { icon: Briefcase, color: 'var(--success)' };
    }
    // Home & Decor
    if (cat.includes('home') || cat.includes('decor') || cat.includes('interior')) {
      return { icon: Home, color: '#14B8A6' };
    }
    // Education & Learning
    if (cat.includes('education') || cat.includes('learning') || cat.includes('teacher')) {
      return { icon: BookOpen, color: '#0EA5E9' };
    }
    // Influencer générique
    if (cat.includes('influencer') || cat.includes('influenceur')) {
      return { icon: Sparkles, color: 'var(--secondary)' };
    }
    // Par défaut
    return { icon: Tag, color: 'var(--text-muted)' };
  };

  // ── Searchable Dropdown (inline component) ──
  const SearchableDropdown = ({ value, onChange, options, placeholder, style }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapRef = useRef(null);

    useEffect(() => {
      const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = options.filter(o => o.toLowerCase().includes(search.toLowerCase()));
    const displayLabel = value || placeholder;

    return (
      <DropdownWrap ref={wrapRef} style={style}>
        <DropdownTrigger
          $active={!!value}
          onClick={() => { setOpen(!open); setSearch(''); }}
          style={{ fontSize: '11px', padding: '7px 6px', minWidth: 0 }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }}>{displayLabel}</span>
          {value && (
            <X size={11} style={{ flexShrink: 0, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }} />
          )}
          <ChevronDown size={11} style={{ flexShrink: 0 }} />
        </DropdownTrigger>
        {open && (
          <DropdownPanel style={{ minWidth: '200px', padding: '4px 0' }}>
            <div style={{ padding: '4px 8px' }}>
              <input
                autoFocus
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                style={{
                  width: '100%', padding: '6px 8px', background: 'var(--surface)', border: '1px solid var(--border)',
                  borderRadius: '6px', color: 'var(--text-primary)', fontSize: '11px', outline: 'none', boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {filtered.length === 0 && (
                <div style={{ padding: '8px 12px', color: 'var(--text-muted)', fontSize: '11px', textAlign: 'center' }}>Aucun résultat</div>
              )}
              {filtered.map((opt, i) => (
                <div
                  key={i}
                  onClick={() => { onChange(opt); setOpen(false); }}
                  style={{
                    padding: '5px 12px', fontSize: '12px', cursor: 'pointer', color: 'var(--text-primary)',
                    background: opt === value ? 'rgba(165,145,239,0.12)' : 'transparent',
                    fontWeight: opt === value ? 600 : 400,
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(165,145,239,0.08)'}
                  onMouseLeave={(e) => e.target.style.background = opt === value ? 'rgba(165,145,239,0.12)' : 'transparent'}
                >
                  {opt}
                </div>
              ))}
            </div>
          </DropdownPanel>
        )}
      </DropdownWrap>
    );
  };

  return (
    <PageContainer>
      <SharedPageHeader icon={SearchIcon} title="Recherche de Profils" subtitle="Explorer et qualifier les profils" />

      <MainLayout>
        <LeftColumn>
          <ResultsContainer>
        
        <SearchInputWrapper>
          <SearchIconWrapper>
            <SearchIcon size={20} />
          </SearchIconWrapper>
          <SearchInput
            type="text"
            placeholder="Rechercher par username, nom complet ou biographie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInputWrapper>

        {/* ── Row 1: Range filters ── */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <RangeFilterGroup>
            <RangeFilterLabel>Followers</RangeFilterLabel>
            <RangeInputs>
              <RangeInput type="number" placeholder="Min" value={followersMin} onChange={(e) => setFollowersMin(e.target.value)} style={{ width: '80px' }} />
              <RangeSeparator>-</RangeSeparator>
              <RangeInput type="number" placeholder="Max" value={followersMax} onChange={(e) => setFollowersMax(e.target.value)} style={{ width: '80px' }} />
            </RangeInputs>
          </RangeFilterGroup>
          <RangeFilterGroup>
            <RangeFilterLabel>Following</RangeFilterLabel>
            <RangeInputs>
              <RangeInput type="number" placeholder="Min" value={followingMin} onChange={(e) => setFollowingMin(e.target.value)} style={{ width: '80px' }} />
              <RangeSeparator>-</RangeSeparator>
              <RangeInput type="number" placeholder="Max" value={followingMax} onChange={(e) => setFollowingMax(e.target.value)} style={{ width: '80px' }} />
            </RangeInputs>
          </RangeFilterGroup>
          <RangeFilterGroup>
            <RangeFilterLabel>Posts</RangeFilterLabel>
            <RangeInputs>
              <RangeInput type="number" placeholder="Min" value={postsMin} onChange={(e) => setPostsMin(e.target.value)} style={{ width: '80px' }} />
              <RangeSeparator>-</RangeSeparator>
              <RangeInput type="number" placeholder="Max" value={postsMax} onChange={(e) => setPostsMax(e.target.value)} style={{ width: '80px' }} />
            </RangeInputs>
          </RangeFilterGroup>
        </div>

        {/* ── Row 2: Compact dropdowns ── */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterSelect value={filterLanguage} onChange={(e) => setFilterLanguage(e.target.value)} style={{ width: '110px', fontSize: '11px', padding: '7px 6px' }}>
            <option value="">Langue</option>
            <option value="fr">Français</option>
            <option value="en">Anglais</option>
            <option value="es">Espagnol</option>
            <option value="de">Allemand</option>
            <option value="it">Italien</option>
            <option value="pt">Portugais</option>
            <option value="ar">Arabe</option>
            <option value="autre">Autre</option>
          </FilterSelect>

          {/* ── Location nested dropdown ── */}
          <DropdownWrap ref={locationRef}>
            <DropdownTrigger
              $active={selectedCountries.length > 0 || selectedCities.length > 0}
              onClick={() => { setLocationOpen(!locationOpen); setCriteriaOpen(false); }}
            >
              <MapPin size={13} />
              {selectedCountries.length > 0 || selectedCities.length > 0
                ? <>{selectedCountries.length > 0 ? selectedCountries.join(', ') : ''}{selectedCities.length > 0 ? (selectedCountries.length > 0 ? ' · ' : '') + selectedCities.join(', ') : ''}</>
                : 'Localisation'}
              {(selectedCountries.length + selectedCities.length) > 0 && <ActiveFilterCount>{selectedCountries.length + selectedCities.length}</ActiveFilterCount>}
              <ChevronDown size={12} />
            </DropdownTrigger>
            {locationOpen && (
              <DropdownPanel style={{ minWidth: '280px' }}>
                {selectedCountries.length > 0 || selectedCities.length > 0 ? (
                  <DropdownSection>
                    <DropdownCheckItem onClick={() => { setSelectedCountries([]); setSelectedCities([]); }} style={{ color: 'var(--danger, #ef4444)', fontWeight: 600 }}>
                      <X size={13} /> Effacer la sélection
                    </DropdownCheckItem>
                  </DropdownSection>
                ) : null}
                {Object.keys(countryCitiesData).sort().map(country => {
                  const isCountrySelected = selectedCountries.includes(country);
                  const isExpanded = expandedCountries[country];
                  const cities = countryCitiesData[country] || [];
                  return (
                    <div key={country}>
                      <CountryRow>
                        <input
                          type="checkbox"
                          checked={isCountrySelected}
                          onChange={() => {
                            setSelectedCountries(prev =>
                              prev.includes(country) ? prev.filter(c => c !== country) : [...prev, country]
                            );
                          }}
                          style={{ width: 14, height: 14, accentColor: 'var(--primary)', cursor: 'pointer' }}
                        />
                        <span style={{ flex: 1 }} onClick={() => setExpandedCountries(prev => ({ ...prev, [country]: !prev[country] }))}>{country}</span>
                        <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{cities.length} ville{cities.length > 1 ? 's' : ''}</span>
                        <span onClick={() => setExpandedCountries(prev => ({ ...prev, [country]: !prev[country] }))} style={{ cursor: 'pointer' }}>
                          {isExpanded ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        </span>
                      </CountryRow>
                      {isExpanded && (
                        <CityList>
                          {cities.map(({ city, count }) => (
                            <DropdownCheckItem key={city}>
                              <input
                                type="checkbox"
                                checked={selectedCities.includes(city)}
                                onChange={() => {
                                  setSelectedCities(prev =>
                                    prev.includes(city) ? prev.filter(c => c !== city) : [...prev, city]
                                  );
                                }}
                              />
                              <span style={{ flex: 1 }}>{city}</span>
                              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{count}</span>
                            </DropdownCheckItem>
                          ))}
                        </CityList>
                      )}
                    </div>
                  );
                })}
                {Object.keys(countryCitiesData).length === 0 && (
                  <div style={{ padding: '12px', color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>Aucune localisation disponible</div>
                )}
              </DropdownPanel>
            )}
          </DropdownWrap>

          <SearchableDropdown value={filterCategory} onChange={setFilterCategory} options={availableCategories} placeholder="Catégorie" />

          <SearchableDropdown value={filterVisitedBy} onChange={setFilterVisitedBy} options={availableVisitedByAccounts} placeholder="Compte" />

          <SearchableDropdown value={filterSource} onChange={setFilterSource} options={availableSources} placeholder="Source" />

          {/* ── Critères combined dropdown ── */}
          <DropdownWrap ref={criteriaRef}>
            <DropdownTrigger
              $active={filterHasLocation || filterHasCategories || filterHasBiography !== 'all' || filterStatus !== 'all' || filterGeminiAnalyzed !== 'all' || filterBusiness !== 'all' || filterAccountType !== 'all' || filterGender !== 'all' || filterEngagement !== 'all' || filterIsTarget !== 'all'}
              onClick={() => { setCriteriaOpen(!criteriaOpen); setLocationOpen(false); }}
            >
              <Filter size={13} />
              Critères
              {(() => {
                let count = 0;
                if (filterHasLocation) count++;
                if (filterHasCategories) count++;
                if (filterHasBiography !== 'all') count++;
                if (filterStatus !== 'all') count++;
                if (filterGeminiAnalyzed !== 'all') count++;
                if (filterBusiness !== 'all') count++;
                if (filterAccountType !== 'all') count++;
                if (filterGender !== 'all') count++;
                if (filterEngagement !== 'all') count++;
                if (filterIsTarget !== 'all') count++;
                return count > 0 ? <ActiveFilterCount>{count}</ActiveFilterCount> : null;
              })()}
              <ChevronDown size={12} />
            </DropdownTrigger>
            {criteriaOpen && (
              <DropdownPanel>
                <DropdownSection>
                  <DropdownSectionTitle>Données remplies</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="checkbox" checked={filterHasLocation} onChange={(e) => setFilterHasLocation(e.target.checked)} />
                    <MapPin size={13} style={{ color: 'var(--primary)' }} /> Localisation remplie
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="checkbox" checked={filterHasCategories} onChange={(e) => setFilterHasCategories(e.target.checked)} />
                    <Tag size={13} style={{ color: 'var(--primary)' }} /> Catégories remplies
                  </DropdownCheckItem>
                </DropdownSection>
                <DropdownSection>
                  <DropdownSectionTitle>Biographie</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="radio" name="bio" checked={filterHasBiography === 'all'} onChange={() => setFilterHasBiography('all')} /> Tous
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="bio" checked={filterHasBiography === 'true'} onChange={() => setFilterHasBiography('true')} /> Avec biographie
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="bio" checked={filterHasBiography === 'false'} onChange={() => setFilterHasBiography('false')} /> Sans biographie
                  </DropdownCheckItem>
                </DropdownSection>
                <DropdownSection>
                  <DropdownSectionTitle>Type de profil</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="radio" name="profileType" checked={filterStatus === 'all'} onChange={() => setFilterStatus('all')} /> Tous les profils
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="profileType" checked={filterStatus === 'all-false'} onChange={() => setFilterStatus('all-false')} />
                    <Eye size={13} /> Publics seulement
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="profileType" checked={filterStatus === 'all-true'} onChange={() => setFilterStatus('all-true')} />
                    <Lock size={13} /> Privés seulement
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="profileType" checked={filterStatus === 'true-all'} onChange={() => setFilterStatus('true-all')} />
                    <Check size={13} /> Vérifiés seulement
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="profileType" checked={filterStatus === 'true-false'} onChange={() => setFilterStatus('true-false')} /> Vérifiés + Publics
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="profileType" checked={filterStatus === 'false-false'} onChange={() => setFilterStatus('false-false')} /> Non vérifiés + Publics
                  </DropdownCheckItem>
                </DropdownSection>
                <DropdownSection>
                  <DropdownSectionTitle>Enrichissement</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="radio" name="gemini" checked={filterGeminiAnalyzed === 'all'} onChange={() => setFilterGeminiAnalyzed('all')} /> Tous
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="gemini" checked={filterGeminiAnalyzed === 'true'} onChange={() => setFilterGeminiAnalyzed('true')} />
                    <Brain size={13} /> Enrichis Gemini
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="gemini" checked={filterGeminiAnalyzed === 'false'} onChange={() => setFilterGeminiAnalyzed('false')} /> Non enrichis
                  </DropdownCheckItem>
                </DropdownSection>
                <DropdownSection>
                  <DropdownSectionTitle>Type de compte (account_type)</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="radio" name="accountType" checked={filterAccountType === 'all'} onChange={() => setFilterAccountType('all')} /> Tous
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="accountType" checked={filterAccountType === 'business'} onChange={() => setFilterAccountType('business')} />
                    <Briefcase size={13} /> Entreprise
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="accountType" checked={filterAccountType === 'personal'} onChange={() => setFilterAccountType('personal')} />
                    <UserCircle2 size={13} /> Personnel (tous)
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="accountType" checked={filterAccountType === 'influenceur'} onChange={() => setFilterAccountType('influenceur')} />
                    <Sparkles size={13} /> Influenceur
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="accountType" checked={filterAccountType === 'créateur'} onChange={() => setFilterAccountType('créateur')} />
                    <Palette size={13} /> Créateur
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="accountType" checked={filterAccountType === 'artiste'} onChange={() => setFilterAccountType('artiste')} />
                    <Music size={13} /> Artiste
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="accountType" checked={filterAccountType === 'personnel'} onChange={() => setFilterAccountType('personnel')} />
                    <User size={13} /> Personnel
                  </DropdownCheckItem>
                </DropdownSection>

                <DropdownSection>
                  <DropdownSectionTitle>Genre</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="radio" name="gender" checked={filterGender === 'all'} onChange={() => setFilterGender('all')} /> Tous
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="gender" checked={filterGender === 'male'} onChange={() => setFilterGender('male')} />
                    <span style={{ fontSize: '14px' }}>♂</span> Homme
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="gender" checked={filterGender === 'female'} onChange={() => setFilterGender('female')} />
                    <span style={{ fontSize: '14px' }}>♀</span> Femme
                  </DropdownCheckItem>
                </DropdownSection>

                <DropdownSection>
                  <DropdownSectionTitle>Engagement</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="radio" name="engagement" checked={filterEngagement === 'all'} onChange={() => setFilterEngagement('all')} /> Tous
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="engagement" checked={filterEngagement === 'high'} onChange={() => setFilterEngagement('high')} />
                    <TrendingUp size={13} style={{ color: 'var(--success)' }} /> Élevé
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="engagement" checked={filterEngagement === 'medium'} onChange={() => setFilterEngagement('medium')} />
                    <TrendingUp size={13} style={{ color: 'var(--warning)' }} /> Moyen
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="engagement" checked={filterEngagement === 'low'} onChange={() => setFilterEngagement('low')} />
                    <TrendingUp size={13} style={{ color: 'var(--text-muted)' }} /> Faible
                  </DropdownCheckItem>
                </DropdownSection>

                <DropdownSection>
                  <DropdownSectionTitle>Statut cible</DropdownSectionTitle>
                  <DropdownCheckItem>
                    <input type="radio" name="isTarget" checked={filterIsTarget === 'all'} onChange={() => setFilterIsTarget('all')} /> Tous
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="isTarget" checked={filterIsTarget === 'true'} onChange={() => setFilterIsTarget('true')} />
                    <Target size={13} style={{ color: 'var(--success)' }} /> Cibles uniquement
                  </DropdownCheckItem>
                  <DropdownCheckItem>
                    <input type="radio" name="isTarget" checked={filterIsTarget === 'false'} onChange={() => setFilterIsTarget('false')} />
                    <Target size={13} style={{ color: 'var(--text-muted)' }} /> Non-cibles uniquement
                  </DropdownCheckItem>
                </DropdownSection>
              </DropdownPanel>
            )}
          </DropdownWrap>

          {/* Date range */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={{ fontSize: '11px', padding: '6px 6px', background: 'var(--surface)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '6px', outline: 'none', width: '115px' }} />
            <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>→</span>
            <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={{ fontSize: '11px', padding: '6px 6px', background: 'var(--surface)', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: '6px', outline: 'none', width: '115px' }} />
          </div>

          <FilterSelect value={limitPerPage} onChange={(e) => setLimitPerPage(parseInt(e.target.value))} style={{ width: '80px', fontSize: '11px', padding: '7px 6px', marginLeft: 'auto' }}>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
            <option value="2000">2000</option>
          </FilterSelect>

          {/* Reset all filters */}
          {(searchTerm || filterLanguage || selectedCountries.length > 0 || selectedCities.length > 0 || filterCategory || filterVisitedBy || filterSource || filterHasLocation || filterHasCategories || filterHasBiography !== 'all' || filterStatus !== 'all' || filterGeminiAnalyzed !== 'all' || filterBusiness !== 'all' || filterAccountType !== 'all' || filterGender !== 'all' || filterEngagement !== 'all' || filterIsTarget !== 'all' || filterDateFrom || filterDateTo || followersMin || followersMax || followingMin || followingMax || postsMin || postsMax) && (
            <button
              onClick={() => {
                setSearchTerm(''); setFilterLanguage(''); setSelectedCountries([]); setSelectedCities([]);
                setFilterCategory(''); setFilterVisitedBy(''); setFilterSource('');
                setFilterHasLocation(false); setFilterHasCategories(false); setFilterHasBiography('all');
                setFilterStatus('all'); setFilterGeminiAnalyzed('all'); setFilterBusiness('all'); setFilterAccountType('all');
                setFilterGender('all'); setFilterEngagement('all'); setFilterIsTarget('all');
                setFilterDateFrom(''); setFilterDateTo('');
                setFollowersMin(''); setFollowersMax(''); setFollowingMin(''); setFollowingMax('');
                setPostsMin(''); setPostsMax('');
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}
            >
              <X size={12} /> Réinitialiser
            </button>
          )}
        </div>

        <ResultsHeader>
          <ResultsCount>
            {loading ? 'Chargement...' : (
              totalResults > loadedResults 
                ? `${loadedResults} profil${loadedResults > 1 ? 's' : ''} chargé${loadedResults > 1 ? 's' : ''} sur ${totalResults} résultat${totalResults > 1 ? 's' : ''}`
                : `${totalResults} résultat${totalResults > 1 ? 's' : ''}`
            )}
          </ResultsCount>
          <button
            onClick={() => {
              if (selectedProfiles.length === profiles.length) {
                setSelectedProfiles([]);
              } else {
                setSelectedProfiles(profiles.map(p => ({
                  ...p,
                  username: p.username,
                  fullName: p.full_name,
                  biography: p.biography,
                  postsCount: p.posts_count,
                  followersCount: p.followers_count,
                  followingCount: p.following_count,
                  isPrivate: p.is_private,
                  isVerified: p.is_verified
                })));
              }
            }}
            style={{
              padding: '6px 14px',
              background: selectedProfiles.length === profiles.length ? 'var(--primary)' : 'var(--surface)',
              border: `1px solid ${selectedProfiles.length === profiles.length ? 'var(--primary)' : 'var(--border)'}`,
              borderRadius: '8px',
              color: selectedProfiles.length === profiles.length ? '#fff' : 'var(--text-primary)',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
          >
            <Check size={16} />
            {selectedProfiles.length === profiles.length ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
        </ResultsHeader>

        <SortBar>
          <SortLabel>Trier par:</SortLabel>
          <SortButton active={sortBy === 'date'} onClick={() => handleSortChange('date')}>
            Date de visite
          </SortButton>
          <SortButton active={sortBy === 'enriched'} onClick={() => handleSortChange('enriched')}>
            Enrichissement
          </SortButton>
          <SortButton active={sortBy === 'followers'} onClick={() => handleSortChange('followers')}>
            Followers
          </SortButton>
          <SortButton active={sortBy === 'following'} onClick={() => handleSortChange('following')}>
            Following
          </SortButton>
          <SortButton active={sortBy === 'posts'} onClick={() => handleSortChange('posts')}>
            Posts
          </SortButton>
          <SortButton active={sortBy === 'username'} onClick={() => handleSortChange('username')}>
            Username
          </SortButton>
        </SortBar>

            {loading ? (
              <EmptyState>⏳ Chargement des profils...</EmptyState>
            ) : profiles.length === 0 ? (
              <EmptyState>
                Aucun profil trouvé
              </EmptyState>
            ) : (
              profiles.map((profile, index) => (
                <ProfileCard 
                  key={profile.username || `profile-${index}`} 
                  style={{ position: 'relative', cursor: 'pointer', borderColor: expandedProfile === profile.username ? 'var(--primary)' : undefined }}
                  onClick={() => setExpandedProfile(expandedProfile === profile.username ? null : profile.username)}
                >
                  <SelectCheckbox 
                    selected={isProfileSelected(profile.username)}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleProfileSelection(profile);
                    }}
                  >
                    {isProfileSelected(profile.username) && <Check size={16} color="white" />}
                  </SelectCheckbox>

                  {/* Expand/collapse indicator */}
                  <div style={{ position: 'absolute', top: '15px', left: '15px', color: 'var(--text-muted)', transition: 'transform .2s', transform: expandedProfile === profile.username ? 'rotate(90deg)' : 'rotate(0)' }}>
                    <ChevronRight size={14} />
                  </div>

              <div style={{ display: 'flex', width: '100%', paddingLeft: '16px', gap: '0' }}>
                {/* COL 1 — Photo + Identité + Bio + Stats (largeur fixe) */}
                <div style={{ width: '420px', minWidth: '420px', display: 'flex', gap: '12px', paddingRight: '16px', borderRight: '1px solid var(--border)' }}>
                  <ProfilePicture>
                    <img
                      src={getLocalProfilePicUrl(profile.username)}
                      alt={profile.username}
                      onError={(e) => {
                        const step = parseInt(e.target.dataset.fallback || '0');
                        if (step === 0) {
                          e.target.dataset.fallback = '1';
                          e.target.src = getProfilePicUrl(profile.username);
                        } else if (step === 1 && profile.profile_pic_url) {
                          e.target.dataset.fallback = '2';
                          e.target.src = profile.profile_pic_url;
                        } else if (step <= 2) {
                          e.target.dataset.fallback = '3';
                          e.target.src = `https://unavatar.io/instagram/${profile.username}`;
                        } else {
                          e.target.style.display = 'none';
                          e.target.parentElement.textContent = profile.username ? profile.username[0].toUpperCase() : '?';
                        }
                      }}
                    />
                  </ProfilePicture>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <Username as="span" onClick={(e) => { e.stopPropagation(); openExternal(`https://www.instagram.com/${profile.username}`); }}>
                        @{profile.username}
                      </Username>
                      <StatusBadge verified={!profile.is_private}>
                        {profile.is_private ? <><Lock size={11} /> Privé</> : <><Eye size={11} /> Public</>}
                      </StatusBadge>
                      {profile.source && (
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, padding: '2px 6px', background: 'var(--surface)', borderRadius: 4, border: '1px solid var(--border)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '160px', display: 'inline-block', verticalAlign: 'middle', lineHeight: '16px' }}>
                          {profile.source}
                        </span>
                      )}
                      {profile.is_verified && <StatusBadge verified={true}>✓</StatusBadge>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '11px' }}>
                      <Calendar size={11} />
                      {formatDate(profile.visited_at)}
                      {profile.visited_by && <><span style={{ margin: '0 2px' }}>·</span><Target size={11} />{profile.visited_by}</>}
                    </div>
                    {profile.biography && (
                      <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: '1.4', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', maxHeight: '34px' }}>
                        {profile.biography}
                      </div>
                    )}
                    <StatsRow>
                      <StatItem><StatLabel>Posts</StatLabel><StatValue>{profile.posts_count?.toLocaleString() || 0}</StatValue></StatItem>
                      <StatItem><StatLabel>Followers</StatLabel><StatValue>{profile.followers_count?.toLocaleString() || 0}</StatValue></StatItem>
                      <StatItem><StatLabel>Following</StatLabel><StatValue>{profile.following_count?.toLocaleString() || 0}</StatValue></StatItem>
                    </StatsRow>
                  </div>
                </div>

                {/* COL 2 — Infos enrichies (localisation, genre, business, engagement) — largeur fixe */}
                <div style={{ width: '200px', minWidth: '200px', padding: '0 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px', borderRight: '1px solid var(--border)' }}>
                  {profile.gemini_analyzed_at && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-muted)', fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '2px' }}>
                      <Sparkles size={10} style={{ color: 'var(--primary)' }} /> Gemini
                    </div>
                  )}
                  {/* Pays + drapeau */}
                  {profile.location_country && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {getCountryFlagUrl(profile.location_country) ? (
                        <img src={getCountryFlagUrl(profile.location_country)} alt="" style={{ width: '20px', height: '14px', objectFit: 'cover', borderRadius: '2px', border: '1px solid var(--border)' }} />
                      ) : (
                        <Globe size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      )}
                      <span style={{ color: 'var(--text-secondary)', fontSize: '12px', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {profile.location_country}
                      </span>
                    </div>
                  )}
                  {/* Ville */}
                  {profile.location_city && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <MapPin size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {profile.location_city}
                      </span>
                    </div>
                  )}
                  {/* Genre — symbole ♂ / ♀ */}
                  {profile.gender && profile.gender !== 'unknown' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '15px', lineHeight: 1 }}>
                        {profile.gender === 'male' ? '♂' : profile.gender === 'female' ? '♀' : '⚥'}
                      </span>
                      <span style={{ color: profile.gender === 'female' ? '#F472B6' : profile.gender === 'male' ? '#60A5FA' : 'var(--text-muted)', fontSize: '12px', fontWeight: '600' }}>
                        {profile.gender === 'male' ? 'Homme' : profile.gender === 'female' ? 'Femme' : 'NB'}
                      </span>
                    </div>
                  )}
                  {/* Account Type (from Gemini: entreprise, influenceur, créateur, artiste, personnel) */}
                  {profile.account_type && (
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                      onClick={() => setFilterAccountType(profile.account_type === 'entreprise' ? 'business' : profile.account_type)}
                      title={`Filtrer par ${profile.account_type}`}
                    >
                      {profile.account_type === 'entreprise' ? <Briefcase size={13} style={{ color: 'var(--warning)', flexShrink: 0 }} /> :
                       profile.account_type === 'influenceur' ? <Sparkles size={13} style={{ color: '#a855f7', flexShrink: 0 }} /> :
                       profile.account_type === 'créateur' ? <Palette size={13} style={{ color: '#3b82f6', flexShrink: 0 }} /> :
                       profile.account_type === 'artiste' ? <Music size={13} style={{ color: '#ec4899', flexShrink: 0 }} /> :
                       <User size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                      <span style={{ fontSize: '11px', fontWeight: '600', color: profile.account_type === 'entreprise' ? 'var(--warning)' : profile.account_type === 'influenceur' ? '#a855f7' : profile.account_type === 'créateur' ? '#3b82f6' : profile.account_type === 'artiste' ? '#ec4899' : 'var(--text-muted)' }}>
                        {profile.account_type.charAt(0).toUpperCase() + profile.account_type.slice(1)}
                      </span>
                    </div>
                  )}
                  {!profile.account_type && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <User size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>
                    </div>
                  )}
                  {/* Engagement */}
                  {profile.engagement_level && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <TrendingUp size={13} style={{ color: profile.engagement_level === 'high' ? 'var(--success)' : profile.engagement_level === 'medium' ? 'var(--warning)' : 'var(--text-muted)', flexShrink: 0 }} />
                      <span style={{ color: profile.engagement_level === 'high' ? 'var(--success)' : profile.engagement_level === 'medium' ? 'var(--warning)' : 'var(--text-muted)', fontSize: '11px', fontWeight: '600' }}>
                        {profile.engagement_level === 'high' ? 'Élevé' : profile.engagement_level === 'medium' ? 'Moyen' : 'Faible'}
                      </span>
                    </div>
                  )}
                  {/* Langue */}
                  {profile.primary_language && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '14px', lineHeight: 1 }}>{getLanguageFlag(profile.primary_language)}</span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600' }}>{profile.primary_language.toUpperCase()}</span>
                    </div>
                  )}
                </div>

                {/* COL 3 — Catégories (flex: 1, remplit le reste) */}
                <div style={{ flex: 1, padding: '0 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px', overflow: 'hidden' }}>
                  {profile.categories && profile.categories.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                      {profile.categories.slice(0, 4).map((cat, idx) => {
                        const { icon: CatIcon, color } = getCategoryIcon(cat);
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 10px', background: 'var(--surface)', borderRadius: '5px', border: `1.5px solid ${color}` }}>
                            <CatIcon size={12} style={{ color, flexShrink: 0 }} />
                            <span style={{ color, fontWeight: '700', fontSize: '11px', whiteSpace: 'nowrap' }}>{cat}</span>
                          </div>
                        );
                      })}
                      {profile.categories.length > 4 && (
                        <div
                          onClick={(e) => { e.stopPropagation(); setShowAllCategories(showAllCategories === profile.username ? null : profile.username); }}
                          style={{ padding: '5px 10px', background: 'var(--surface)', borderRadius: '5px', border: '1.5px solid var(--primary)', cursor: 'pointer' }}>
                          <span style={{ color: 'var(--primary)', fontWeight: '700', fontSize: '11px' }}>+{profile.categories.length - 4}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)', fontSize: '11px', fontStyle: 'italic' }}>Aucune catégorie</span>
                  )}
                  {profile.main_category && (
                    <span style={{ color: 'var(--text-muted)', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {profile.main_category}
                    </span>
                  )}
                </div>

                {/* COL 4 — Indicateur cible (petite colonne fixe) */}
                <div style={{ width: '50px', minWidth: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {profile.is_target && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', padding: '6px', background: profile.target_status === 'active' ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)', borderRadius: '6px', border: `2px solid ${profile.target_status === 'active' ? 'var(--success)' : 'var(--text-muted)'}` }}>
                      <Target size={14} style={{ color: profile.target_status === 'active' ? 'var(--success)' : 'var(--text-muted)' }} />
                      <span style={{ color: profile.target_status === 'active' ? 'var(--success)' : 'var(--text-muted)', fontSize: '8px', fontWeight: '700', textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.2, whiteSpace: 'pre-line' }}>
                        {profile.target_status === 'active' ? 'Cible\nActive' : 'Cible'}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal catégories */}
              {showAllCategories === profile.username && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
                  onClick={() => setShowAllCategories(null)}>
                  <div style={{ background: 'var(--card-bg)', borderRadius: '12px', padding: '24px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', border: '2px solid var(--primary)' }}
                    onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                      <h3 style={{ color: 'var(--text-secondary)', fontSize: '18px', fontWeight: '700', margin: 0 }}>Catégories de @{profile.username}</h3>
                      <X size={24} style={{ color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => setShowAllCategories(null)} />
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {profile.categories && profile.categories.map((cat, idx) => {
                        const { icon: CatIcon, color } = getCategoryIcon(cat);
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'var(--surface)', borderRadius: '8px', border: `2px solid ${color}` }}>
                            <CatIcon size={16} style={{ color, flexShrink: 0 }} />
                            <span style={{ color, fontWeight: '700', fontSize: '14px' }}>{cat}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* ── EXPANDED DETAIL PANEL ── */}
              {expandedProfile === profile.username && (
                <div 
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    marginTop: '12px',
                    padding: '16px',
                    background: 'var(--surface)',
                    borderRadius: '10px',
                    border: '1px solid rgba(165,145,239,.2)',
                    display: 'grid',
                    gridTemplateColumns: '220px 1fr 1fr',
                    gap: '20px',
                    animation: 'fadeIn .2s ease-out'
                  }}
                >
                  {/* Screenshot */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                      Screenshot du profil
                    </div>
                    <div style={{
                      width: '100%',
                      aspectRatio: '9/16',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid var(--border)',
                      background: 'var(--bg)',
                      position: 'relative'
                    }}>
                      <img
                        src={getScreenshotUrl(profile.username)}
                        alt={`Screenshot @${profile.username}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          const img = e.target;
                          if (!img.dataset.fallback) {
                            img.dataset.fallback = '1';
                            img.src = `/api/media/profile-pic/${profile.username}.jpg`;
                          } else {
                            img.style.display = 'none';
                            img.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:var(--text-muted);font-size:12px;text-align:center;padding:20px">Aucun screenshot disponible</div>';
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Detail info */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {/* Full name */}
                    {profile.full_name && (
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '4px' }}>Nom complet</div>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>{profile.full_name}</div>
                      </div>
                    )}

                    {/* Gemini summary */}
                    {profile.gemini_summary && (
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Sparkles size={12} style={{ color: 'var(--primary)' }} />
                          Résumé Gemini AI
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', background: 'rgba(165,145,239,.05)', padding: '10px 14px', borderRadius: '8px', border: '1px solid rgba(165,145,239,.1)' }}>
                          {profile.gemini_summary}
                        </div>
                      </div>
                    )}

                    {/* Info grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                      {profile.phone_number && (
                        <div style={{ padding: '10px 12px', background: 'rgba(34,197,94,.08)', borderRadius: '8px', border: '1px solid rgba(34,197,94,.2)' }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '2px' }}>Téléphone</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--success)' }}>{profile.phone_number}</div>
                        </div>
                      )}
                      {profile.main_category && (
                        <div style={{ padding: '10px 12px', background: 'rgba(165,145,239,.08)', borderRadius: '8px', border: '1px solid rgba(165,145,239,.2)' }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '2px' }}>Catégorie principale</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--primary)' }}>{profile.main_category}</div>
                        </div>
                      )}
                      {profile.location_region && (
                        <div style={{ padding: '10px 12px', background: 'rgba(59,130,246,.08)', borderRadius: '8px', border: '1px solid rgba(59,130,246,.2)' }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '2px' }}>Région</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--info)' }}>{profile.location_region}</div>
                        </div>
                      )}
                      <div style={{ padding: '10px 12px', background: 'rgba(245,158,11,.08)', borderRadius: '8px', border: '1px solid rgba(245,158,11,.2)' }}>
                        <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '2px' }}>Visites</div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#f59e0b' }}>{profile.visit_count || 1}x</div>
                      </div>
                      {profile.gemini_analyzed_at && (
                        <div style={{ padding: '10px 12px', background: 'rgba(165,145,239,.08)', borderRadius: '8px', border: '1px solid rgba(165,145,239,.2)' }}>
                          <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '2px' }}>Analysé le</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--primary)' }}>{new Date(profile.gemini_analyzed_at).toLocaleDateString('fr-FR')}</div>
                        </div>
                      )}
                    </div>

                    {/* Biography (if not shown above) */}
                    {profile.biography && (
                      <div>
                        <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', marginBottom: '4px' }}>Biographie</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>{profile.biography}</div>
                      </div>
                    )}
                  </div>

                  {/* ── ACTIONS HISTORY COLUMN ── */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflow: 'hidden' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                      <Activity size={12} style={{ color: 'var(--primary)' }} />
                      Historique des actions
                    </div>

                    {profileActionsLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', fontSize: '12px', padding: '20px 0' }}>
                        <Loader size={14} className="spin" /> Chargement...
                      </div>
                    ) : !profileActions?.actions?.length ? (
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', padding: '20px 0', textAlign: 'center' }}>
                        Aucune action enregistrée
                      </div>
                    ) : (
                      <>
                        {/* Summary badges */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                          {Object.entries(profileActions.summary || {}).map(([type, count]) => {
                            const icons = { FollowAction: UserPlus, UnfollowAction: UserMinus, LikeAction: Heart, CommentAction: MessageCircle, DirectMessageAction: MessageCircle, GetProfileAction: Eye, StoryWatchAction: Eye, StoryLikeAction: Heart };
                            const colors = { FollowAction: '#22c55e', UnfollowAction: '#ef4444', LikeAction: '#ec4899', CommentAction: '#3b82f6', DirectMessageAction: '#8b5cf6', GetProfileAction: '#64748b', StoryWatchAction: '#f59e0b', StoryLikeAction: '#f59e0b' };
                            const Icon = icons[type] || Activity;
                            const color = colors[type] || 'var(--text-muted)';
                            const label = type.replace('Action', '');
                            return (
                              <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 8px', borderRadius: '6px', background: `${color}15`, border: `1px solid ${color}30`, fontSize: '11px', fontWeight: '600', color }}>
                                <Icon size={10} /> {label} <span style={{ opacity: .7 }}>×{count}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Action list */}
                        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          {profileActions.actions.slice(0, 30).map((action, i) => {
                            const colors = { FollowAction: '#22c55e', UnfollowAction: '#ef4444', LikeAction: '#ec4899', CommentAction: '#3b82f6', DirectMessageAction: '#8b5cf6', GetProfileAction: '#64748b', StoryWatchAction: '#f59e0b', StoryLikeAction: '#f59e0b' };
                            const color = colors[action.type] || 'var(--text-muted)';
                            return (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 8px', borderRadius: '6px', background: 'var(--bg)', fontSize: '11px' }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                                <div style={{ flex: 1, color: 'var(--text-secondary)', fontWeight: '500' }}>
                                  {action.type.replace('Action', '')}
                                </div>
                                {action.actor_username && (
                                  <div style={{ color: 'var(--primary)', fontSize: '10px', fontWeight: '600' }}>@{action.actor_username}</div>
                                )}
                                <div style={{ color: 'var(--text-muted)', fontSize: '10px', whiteSpace: 'nowrap' }}>
                                  {action.timestamp ? new Date(action.timestamp).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : '—'}
                                </div>
                              </div>
                            );
                          })}
                          {profileActions.actions.length > 30 && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center', padding: '4px' }}>
                              +{profileActions.actions.length - 30} autres actions
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                </div>
              )}

                </ProfileCard>
              ))
            )}
          </ResultsContainer>
        </LeftColumn>

        <RightColumn>
          {/* ── STATS PANEL ── */}
          {profileStats && (
            <div style={{ background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--border)', padding: '16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Zap size={14} style={{ color: 'var(--primary)' }} /> Statistiques ({totalResults})
              </div>

              {/* Gender row */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <div style={{ flex: 1, background: 'var(--surface)', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid rgba(96,165,250,0.3)', cursor: 'pointer', opacity: filterGender === 'male' ? 1 : 0.7 }} onClick={() => setFilterGender(filterGender === 'male' ? 'all' : 'male')}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#60A5FA' }}>{profileStats.gender?.male || 0}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600' }}>♂ Hommes</div>
                </div>
                <div style={{ flex: 1, background: 'var(--surface)', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid rgba(244,114,182,0.3)', cursor: 'pointer', opacity: filterGender === 'female' ? 1 : 0.7 }} onClick={() => setFilterGender(filterGender === 'female' ? 'all' : 'female')}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: '#F472B6' }}>{profileStats.gender?.female || 0}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600' }}>♀ Femmes</div>
                </div>
                <div style={{ flex: 1, background: 'var(--surface)', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-muted)' }}>{profileStats.gender?.unknown || 0}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600' }}>? Inconnu</div>
                </div>
              </div>

              {/* Account type breakdown */}
              {profileStats.accountType && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '10px' }}>
                  <div onClick={() => setFilterAccountType('business')} style={{ cursor: 'pointer', flex: '1 0 30%', background: 'var(--surface)', borderRadius: '6px', padding: '6px 4px', textAlign: 'center', border: filterAccountType === 'business' ? '2px solid var(--warning)' : '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--warning)' }}>{profileStats.accountType.entreprise || 0}</div>
                    <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontWeight: '600' }}>Entreprise</div>
                  </div>
                  <div onClick={() => setFilterAccountType('influenceur')} style={{ cursor: 'pointer', flex: '1 0 30%', background: 'var(--surface)', borderRadius: '6px', padding: '6px 4px', textAlign: 'center', border: filterAccountType === 'influenceur' ? '2px solid #a855f7' : '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#a855f7' }}>{profileStats.accountType.influenceur || 0}</div>
                    <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontWeight: '600' }}>Influenceur</div>
                  </div>
                  <div onClick={() => setFilterAccountType('créateur')} style={{ cursor: 'pointer', flex: '1 0 30%', background: 'var(--surface)', borderRadius: '6px', padding: '6px 4px', textAlign: 'center', border: filterAccountType === 'créateur' ? '2px solid #3b82f6' : '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#3b82f6' }}>{profileStats.accountType.createur || 0}</div>
                    <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontWeight: '600' }}>Créateur</div>
                  </div>
                  <div onClick={() => setFilterAccountType('artiste')} style={{ cursor: 'pointer', flex: '1 0 30%', background: 'var(--surface)', borderRadius: '6px', padding: '6px 4px', textAlign: 'center', border: filterAccountType === 'artiste' ? '2px solid #ec4899' : '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#ec4899' }}>{profileStats.accountType.artiste || 0}</div>
                    <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontWeight: '600' }}>Artiste</div>
                  </div>
                  <div onClick={() => setFilterAccountType('personnel')} style={{ cursor: 'pointer', flex: '1 0 30%', background: 'var(--surface)', borderRadius: '6px', padding: '6px 4px', textAlign: 'center', border: filterAccountType === 'personnel' ? '2px solid var(--text-muted)' : '1px solid var(--border)' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-muted)' }}>{profileStats.accountType.perso || 0}</div>
                    <div style={{ fontSize: '8px', color: 'var(--text-muted)', fontWeight: '600' }}>Personnel</div>
                  </div>
                </div>
              )}

              {/* Quick stats grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '10px' }}>
                <div style={{ background: 'var(--surface)', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--success)' }}>{profileStats.verified || 0}</div>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', fontWeight: '600' }}>Vérifiés</div>
                </div>
                <div style={{ background: 'var(--surface)', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--info)' }}>{profileStats.withLocation || 0}</div>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', fontWeight: '600' }}>Localisés</div>
                </div>
                <div style={{ background: 'var(--surface)', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--primary)' }}>{profileStats.geminiAnalyzed || 0}</div>
                  <div style={{ fontSize: '9px', color: 'var(--text-muted)', fontWeight: '600' }}>Gemini</div>
                </div>
              </div>

              {/* Averages */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
                <div style={{ flex: 1, background: 'var(--surface)', borderRadius: '6px', padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)' }}>{(profileStats.averages?.followers || 0).toLocaleString()}</div>
                  <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Moy. Followers</div>
                </div>
                <div style={{ flex: 1, background: 'var(--surface)', borderRadius: '6px', padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)' }}>{(profileStats.averages?.following || 0).toLocaleString()}</div>
                  <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Moy. Following</div>
                </div>
                <div style={{ flex: 1, background: 'var(--surface)', borderRadius: '6px', padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-secondary)' }}>{(profileStats.averages?.posts || 0).toLocaleString()}</div>
                  <div style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Moy. Posts</div>
                </div>
              </div>

              {/* Engagement breakdown */}
              {(profileStats.engagement?.high > 0 || profileStats.engagement?.medium > 0 || profileStats.engagement?.low > 0) && (
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Engagement</div>
                  <div style={{ display: 'flex', gap: '4px', height: '6px', borderRadius: '3px', overflow: 'hidden', background: 'var(--surface)' }}>
                    {profileStats.engagement?.high > 0 && <div style={{ width: `${(profileStats.engagement.high / totalResults) * 100}%`, background: 'var(--success)', borderRadius: '3px' }} title={`Élevé: ${profileStats.engagement.high}`} />}
                    {profileStats.engagement?.medium > 0 && <div style={{ width: `${(profileStats.engagement.medium / totalResults) * 100}%`, background: 'var(--warning)', borderRadius: '3px' }} title={`Moyen: ${profileStats.engagement.medium}`} />}
                    {profileStats.engagement?.low > 0 && <div style={{ width: `${(profileStats.engagement.low / totalResults) * 100}%`, background: 'var(--text-muted)', borderRadius: '3px' }} title={`Faible: ${profileStats.engagement.low}`} />}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '3px', fontSize: '9px', color: 'var(--text-muted)' }}>
                    <span><span style={{ color: 'var(--success)' }}>●</span> {profileStats.engagement?.high || 0} élevé</span>
                    <span><span style={{ color: 'var(--warning)' }}>●</span> {profileStats.engagement?.medium || 0} moyen</span>
                    <span><span style={{ color: 'var(--text-muted)' }}>●</span> {profileStats.engagement?.low || 0} faible</span>
                  </div>
                </div>
              )}

              {/* Top categories */}
              {topCategories.length > 0 && (
                <div style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Top Catégories</div>
                  {topCategories.slice(0, 6).map((cat, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '3px 0', cursor: 'pointer' }}
                      onClick={() => setFilterCategory(filterCategory === cat.name ? '' : cat.name)}>
                      <span style={{ fontSize: '11px', color: filterCategory === cat.name ? 'var(--primary)' : 'var(--text-secondary)', fontWeight: filterCategory === cat.name ? '700' : '500' }}>{cat.name}</span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600', background: 'var(--surface)', padding: '1px 6px', borderRadius: '8px' }}>{cat.count}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Top countries */}
              {topCountries.length > 0 && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Top Pays</div>
                  {topCountries.slice(0, 5).map((c, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0' }}>
                      {getCountryFlagUrl(c.name) ? (
                        <img src={getCountryFlagUrl(c.name)} alt="" style={{ width: '16px', height: '11px', objectFit: 'cover', borderRadius: '1px', border: '1px solid var(--border)' }} />
                      ) : (
                        <Globe size={11} style={{ color: 'var(--text-muted)' }} />
                      )}
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)', flex: 1 }}>{c.name}</span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '600', background: 'var(--surface)', padding: '1px 6px', borderRadius: '8px' }}>{c.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <MapView profiles={profiles} />

          <SearchBox>
            <PanelTitle>
              <Target size={24} />
              Sélection de Cibles
            </PanelTitle>
            
            {selectedProfiles.length === 0 ? (
              <EmptySelection>
                Sélectionnez des profils pour les ajouter comme cibles
              </EmptySelection>
            ) : (
              <>
                <SelectedProfilesList>
                  {selectedProfiles.map((profile, index) => (
                    <SelectedProfileItem key={profile.username || `selected-${index}`}>
                      <ProfileName>@{profile.username}</ProfileName>
                      <RemoveButton onClick={() => removeSelectedProfile(profile.username)}>
                        <Trash2 size={14} />
                      </RemoveButton>
                    </SelectedProfileItem>
                  ))}
                </SelectedProfilesList>
                
                <AddTargetsButton 
                  onClick={moveToTemporaryTargets}
                  disabled={false}
                >
                  <Plus size={20} />
                  Ajouter à la sélection
                </AddTargetsButton>
              </>
            )}
          </SearchBox>
          
          {temporaryTargets.length > 0 && (
          <TargetPanel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <Target size={24} style={{ color: 'var(--primary)' }} />
              <h2 style={{ color: 'var(--primary)', fontSize: '20px', fontWeight: '700', margin: 0 }}>Sélection de Cibles</h2>
            </div>
          
            <TemporaryTargetsPanel>
              <PanelTitle style={{ color: 'var(--primary)' }}>
                <Check size={24} />
                Cibles à Ajouter ({temporaryTargets.length})
              </PanelTitle>
              
              <ActionSelector>
                <ActionOption $active={selectedAction === 'add-targets'} onClick={() => setSelectedAction('add-targets')}>
                  <RadioButton type="radio" name="action" value="add-targets" checked={selectedAction === 'add-targets'} onChange={(e) => setSelectedAction(e.target.value)} />
                  <Target size={20} style={{ color: selectedAction === 'add-targets' ? 'var(--primary)' : 'var(--text-secondary)' }} />
                  <ActionLabel>Cibles</ActionLabel>
                  <ActionDescription>Ajouter comme cibles d'interaction</ActionDescription>
                </ActionOption>
                
                <ActionOption $active={selectedAction === 'send-to-crm'} onClick={() => setSelectedAction('send-to-crm')}>
                  <RadioButton type="radio" name="action" value="send-to-crm" checked={selectedAction === 'send-to-crm'} onChange={(e) => setSelectedAction(e.target.value)} />
                  <Contact size={20} style={{ color: selectedAction === 'send-to-crm' ? 'var(--primary)' : 'var(--text-secondary)' }} />
                  <ActionLabel>CRM</ActionLabel>
                  <ActionDescription>Importer comme prospects</ActionDescription>
                </ActionOption>
                
                <ActionOption $active={selectedAction === 'google-sheets'} onClick={() => setSelectedAction('google-sheets')}>
                  <RadioButton type="radio" name="action" value="google-sheets" checked={selectedAction === 'google-sheets'} onChange={(e) => setSelectedAction(e.target.value)} />
                  <Download size={20} style={{ color: selectedAction === 'google-sheets' ? 'var(--primary)' : 'var(--text-secondary)' }} />
                  <ActionLabel>Sheets</ActionLabel>
                  <ActionDescription>Exporter vers Google Sheets</ActionDescription>
                </ActionOption>
                
                <ActionOption $active={selectedAction === 'analyze-gemini'} onClick={() => setSelectedAction('analyze-gemini')}>
                  <RadioButton type="radio" name="action" value="analyze-gemini" checked={selectedAction === 'analyze-gemini'} onChange={(e) => setSelectedAction(e.target.value)} />
                  <Brain size={20} style={{ color: selectedAction === 'analyze-gemini' ? 'var(--primary)' : 'var(--text-secondary)' }} />
                  <ActionLabel>Gemini AI</ActionLabel>
                  <ActionDescription>Enrichir avec IA</ActionDescription>
                </ActionOption>
                
                <ActionOption $active={selectedAction === 'interact-list'} onClick={() => setSelectedAction('interact-list')}>
                  <RadioButton type="radio" name="action" value="interact-list" checked={selectedAction === 'interact-list'} onChange={(e) => setSelectedAction(e.target.value)} />
                  <Users size={20} style={{ color: selectedAction === 'interact-list' ? '#22c55e' : 'var(--text-secondary)' }} />
                  <ActionLabel>Interact</ActionLabel>
                  <ActionDescription>Ajouter à la liste d'interaction</ActionDescription>
                </ActionOption>
              </ActionSelector>
              
              {(selectedAction === 'add-targets' || selectedAction === 'interact-list') && (
                <>
                  <SelectorLabel>Compte Instagram</SelectorLabel>
                  <AccountSelector 
                    value={selectedAccountId} 
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                  >
                    {accounts.length === 0 ? (
                      <option value="">Aucun compte disponible</option>
                    ) : (
                      accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          @{account.username || account.instagramUsername}
                        </option>
                      ))
                    )}
                  </AccountSelector>
                </>
              )}
              
              <TemporaryTargetsList>
                {temporaryTargets.map((target, index) => (
                  <TemporaryTargetItem key={target.username || `temp-${index}`}>
                    <ProfileName>@{target.username}</ProfileName>
                    <RemoveButton onClick={() => removeTemporaryTarget(target.username)}>
                      <Trash2 size={14} />
                    </RemoveButton>
                  </TemporaryTargetItem>
                ))}
              </TemporaryTargetsList>
              
              <ConfirmTargetsButton 
                onClick={confirmTargets}
                disabled={addingTargets}
              >
                {selectedAction === 'send-to-crm' ? (
                  <>
                    <Contact size={20} />
                    {addingTargets ? 'Import CRM en cours...' : `Importer ${temporaryTargets.length} prospect(s)`}
                  </>
                ) : selectedAction === 'google-sheets' ? (
                  <>
                    <Download size={20} />
                    {addingTargets ? 'Export en cours...' : `Exporter ${temporaryTargets.length} profil(s)`}
                  </>
                ) : selectedAction === 'analyze-gemini' ? (
                  <>
                    <Brain size={20} />
                    {addingTargets ? 'Analyse en cours...' : `Analyser ${temporaryTargets.length} profil(s) avec Gemini`}
                  </>
                ) : selectedAction === 'interact-list' ? (
                  <>
                    <Users size={20} />
                    {addingTargets ? 'Ajout en cours...' : `Ajouter ${temporaryTargets.length} profil(s) à la liste d'interaction`}
                  </>
                ) : (
                  <>
                    <Check size={20} />
                    {addingTargets ? 'Ajout en cours...' : `Confirmer et ajouter ${temporaryTargets.length} cible(s)`}
                  </>
                )}
              </ConfirmTargetsButton>
            </TemporaryTargetsPanel>
          </TargetPanel>
          )}
        </RightColumn>
      </MainLayout>
    </PageContainer>
  );
}

export default Search;
